import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * A trainer's own panels, in one request.
 *
 * Separate from `['dashboard']` rather than folded into it, because the main
 * payload is fetched by every role and these three queries are for one. The
 * key is a sibling so `invalidateQueries(['dashboard'])` still refreshes it
 * after a grading action.
 */
export interface TrainerCourse {
  id: string;
  title: string;
  status: string;
  thumbnailUrl: string | null;
  color: string | null;
  durationMinutes: number | null;
  modules: number;
  enrolled: number;
  /** Null when nobody is enrolled — not 0. */
  completionPercent: number | null;
  /** Null until somebody rates it. */
  rating: number | null;
}

export interface TrainerKpis {
  /** Every course they have written, in any status. */
  coursesCreated: number;
  published: number;
  assessmentsCreated: number;
  assessmentsPublished: number;
  compliance: {
    peopleCompliant: number;
    peopleTotal: number;
    percent: number;
    total: number;
    completed: number;
  };
}

export interface TrainerDesk {
  courses: TrainerCourse[];
  kpis: TrainerKpis;
}

export function useTrainerDesk(enabled: boolean) {
  return useQuery({
    queryKey: ['dashboard', 'trainer'],
    queryFn: async () => {
      const { data } = await api.get<TrainerDesk>('/dashboard/trainer');
      return data;
    },
    /* Never fired for an administrator or an employee — the endpoint would
       403 them, and a failed request behind a panel they cannot see is noise
       in the console and a wasted round trip. */
    enabled,
  });
}
